/**
 * Events, games, public meetings and the AEG venues.
 *
 * Denver publishes no events dataset — all 1,260 of its ArcGIS services carry
 * nothing current — so this is four sources merged by the server: Ticketmaster
 * Discovery, ESPN for fixtures, Legistar for council business, and scraped
 * listings for the AXS venues Ticketmaster does not sell for. That is a real
 * limit worth stating rather than hiding: a street fair or a protest will
 * still not be here.
 *
 * The scraped source will break — that is what scrapers do. When it does, the
 * server names it in `degraded` and this layer says so in the panel and the
 * legend. A quiet Red Rocks must never be indistinguishable from an unread
 * one.
 *
 * Drawn as tall flags. Everything else on this map sits on the ground; an
 * event is a thing that will happen at a place, and a flag planted above the
 * rooftops is visible from across a pitched 3D city in a way a dot on the
 * street never is.
 */

import { IconLayer, TextLayer } from "@deck.gl/layers";

import { footprints } from "../map/footprints.js";
import { Layer } from "./base.js";

/**
 * Clearance above a rooftop, in metres.
 *
 * Sitting exactly on the roof puts the marker in the same plane as the surface
 * it stands on, which z-fights and tears as the camera moves. Two metres is
 * under a storey, so it still reads as belonging to that roof.
 */
const ROOF_CLEARANCE_M = 2;

/** A rooftop height turned into a position to sit at, with clearance. */
const lift = (height) => (height > 0 ? height + ROOF_CLEARANCE_M : 0);


import { palette, themeName } from "../theme/palette.js";
import { OVER_EVERYTHING } from "../map/depth.js";

/** Warm violet — adjacent to the mark palette, distinct from incident teal. */

/** Flag bitmap. Tall and narrow: it is mostly pole. */
const FLAG_W = 96;
const FLAG_H = 224;

/** The pole's foot, which is the point the flag actually marks. */
const FLAG_ANCHOR_Y = FLAG_H - 4;

let flagIcon = null;
let flagTheme = null;

/**
 * Draw the flag once and cache it.
 *
 * Built lazily rather than at module load: this file is imported by the test
 * suite under Node, where there is no `document`. Cached as a bitmap, so the
 * cache is keyed on the theme that drew it — an event colour from the old
 * theme flying over the new one was exactly the mixed state a theme switch
 * must not leave.
 */
function flag() {
  const theme = themeName();
  if (flagIcon && flagTheme === theme) return flagIcon;
  flagTheme = theme;

  const canvas = document.createElement("canvas");
  canvas.width = FLAG_W;
  canvas.height = FLAG_H;
  const ctx = canvas.getContext("2d");
  const x = 20;
  const rgba = (a) => `rgba(${palette().event[0]},${palette().event[1]},${palette().event[2]},${a})`;

  // Pole: brightest at the base where it meets the ground, fading upward, so
  // it reads as planted rather than floating.
  const pole = ctx.createLinearGradient(0, FLAG_H, 0, 0);
  pole.addColorStop(0, rgba(0.95));
  pole.addColorStop(1, rgba(0.45));
  ctx.fillStyle = pole;
  ctx.fillRect(x - 2, 10, 4, FLAG_H - 14);

  // Pennant, with a notched trailing edge so it is a flag and not a rectangle.
  ctx.beginPath();
  ctx.moveTo(x + 2, 16);
  ctx.lineTo(x + 62, 40);
  ctx.lineTo(x + 44, 58);
  ctx.lineTo(x + 62, 76);
  ctx.lineTo(x + 2, 92);
  ctx.closePath();
  ctx.fillStyle = rgba(0.82);
  ctx.fill();
  ctx.strokeStyle = rgba(1);
  ctx.lineWidth = 2;
  ctx.stroke();

  // A small pool of light at the foot, to seat it on the surface.
  const base = ctx.createRadialGradient(x, FLAG_H - 6, 0, x, FLAG_H - 6, 26);
  base.addColorStop(0, rgba(0.5));
  base.addColorStop(1, rgba(0));
  ctx.fillStyle = base;
  ctx.fillRect(x - 26, FLAG_H - 32, 52, 32);

  flagIcon = {
    url: canvas.toDataURL(),
    width: FLAG_W,
    height: FLAG_H,
    anchorX: x,
    anchorY: FLAG_ANCHOR_Y,
    mask: false,
  };
  return flagIcon;
}

const DAY_MS = 86400000;

/** Events sooner than this are drawn brighter; they are the ones that matter. */
const SOON_DAYS = 3;

function startMs(event) {
  const raw = event?.start;
  if (!raw) return NaN;
  const t = Date.parse(raw.length === 10 ? `${raw}T00:00:00Z` : raw);
  return Number.isFinite(t) ? t : NaN;
}

/** "Tonight", "Tomorrow", "Sat 2 Aug" — how a person says when. */
function whenLabel(event) {
  const t = startMs(event);
  if (!Number.isFinite(t)) return "date unknown";

  const now = Date.now();
  const days = Math.floor((t - now) / DAY_MS);
  const time = event.local_time
    ? new Date(`1970-01-01T${event.local_time}`).toLocaleTimeString(undefined,
        { hour: "2-digit", minute: "2-digit", hour12: false })
    : null;

  if (days < 0) return "in progress or past";
  if (days === 0) return time ? `today ${time}` : "today";
  if (days === 1) return time ? `tomorrow ${time}` : "tomorrow";
  const date = new Date(t).toLocaleDateString(undefined,
    { weekday: "short", month: "short", day: "numeric" });
  return time ? `${date} ${time}` : date;
}

export class EventLayer extends Layer {
  static key = "events";
  static label = "Events";
  static description = "Events, games & public meetings, next 24 hours";

  /**
   * Matched to the server's 30-minute cache. Worth polling less for new shows
   * than for the scraped venues going down: `degraded` is the layer admitting
   * it cannot see Red Rocks, and a page open all evening should be able to
   * start saying so. At 9.6 KB it is by far the cheapest source here.
   */
  static refreshMs = 30 * 60 * 1000;

  async load() {
    this.status = "loading";
    try {
      const response = await fetch(`${this.basePath}/events`);
      if (!response.ok) throw new Error(String(response.status));
      const body = await response.json();
      this._events = body.events ?? [];
      this._configured = body.configured !== false;
      // Sources the server could not read at all. The AXS venues are
      // scraped, so this is not hypothetical — it is the expected end state
      // of any scraper, and a night at Red Rocks that silently reads as
      // "nothing on" is worse than one that admits it does not know.
      this._degraded = body.degraded ?? [];

      if (!this._configured) {
        // Distinguish "not set up" from "nothing on". They need different
        // responses from whoever is reading the panel.
        this.status = "unavailable";
        this.detail = "No API key set";
        return null;
      }
      this.status = this._events.length ? "ready" : "unavailable";
      this.detail = this._events.length ? `${this._events.length} upcoming` : "None scheduled";
      if (this._degraded.length) {
        // Said in the panel, not just carried in the JSON. An unread status
        // field is not a loud failure.
        this.detail += ` · ${this._degraded.join(", ")} unavailable`;
      }
      return null;
    } catch (error) {
      this.status = "unavailable";
      this.detail = `Unavailable (${error.message})`;
      return null;
    }
  }

  get loadedCount() {
    return this._events?.length ?? 0;
  }

  legend() {
    const note = "Ticketed events, games & public meetings. Taller flag = within three days.";
    return [{
      rgb: palette().event,
      label: "Event",
      note: this._degraded?.length
        ? `${note} NOT SHOWN right now: ${this._degraded.join(", ")} — that source could not be read, so this map is incomplete.`
        : note,
    }];
  }

  describe(event) {
    const rows = [
      ["When", whenLabel(event)],
      ["Venue", event.venue || "—"],
    ];
    if (event.genre) rows.push(["Kind", event.genre]);
    else if (event.segment) rows.push(["Kind", event.segment]);
    if (event.local_date) rows.push(["Date", event.local_date]);
    if (event.occurrences > 1) {
      rows.push(["Runs", `${event.occurrences} dates${event.runs_until ? `, through ${event.runs_until}` : ""}`]);
    }
    // Each row names where it came from: the server merges Ticketmaster,
    // ESPN games and Legistar council meetings into this one stream.
    rows.push(["Source", event.source || "Ticketmaster Discovery"]);
    return { title: event.name || "Event", rows };
  }

  /** Supplied by the app: height of the building under a point, or 0. */
  groundHeight = null;

  render() {
    if (!this.enabled || this.status !== "ready") {
      this.visibleCount = 0;
      return [];
    }
    const data = this._events;
    this.visibleCount = data.length;

    const soon = Date.now() + SOON_DAYS * DAY_MS;
    const icon = flag();

    return [
      new IconLayer({
        id: `${this.key}-flags`,
        data,
        getIcon: () => icon,
        // A venue is a building, so at ground level the flag plants itself
        // inside the extrusion and the pole disappears into it. Stood on the
        // roof, the way marks are.
        getPosition: (d) => [d.lng, d.lat, lift(this.groundHeight?.(d))],
        updateTriggers: { getPosition: footprints.stamp, getIcon: themeName() },
        // Imminent events fly a taller flag; distant ones stay out of the way.
        getSize: (d) => (startMs(d) <= soon ? 82 : 58),
        getColor: (d) => [255, 255, 255, startMs(d) <= soon ? 255 : 165],
        sizeUnits: "pixels",
        // A floor on the size, so a flag never shrinks to a smudge — it is
        // marking something that happens at a place, and has to stay findable.
        sizeMinPixels: 40,
        sizeMaxPixels: 150,
        billboard: true,
        pickable: true,
        parameters: OVER_EVERYTHING,
      }),
      new TextLayer({
        id: `${this.key}-labels`,
        // Only the imminent ones get a label; thirty days of venue names at
        // once is a wall of text, not information.
        data: data.filter((d) => startMs(d) <= soon),
        getPosition: (d) => [d.lng, d.lat, lift(this.groundHeight?.(d))],
        updateTriggers: { getPosition: footprints.stamp },
        getText: (d) => d.venue || d.name || "",
        getSize: 10,
        getColor: [...palette().event, 230],
        getPixelOffset: [0, -84],
        fontFamily: "ui-monospace, Menlo, Consolas, monospace",
        characterSet: "auto",
        outlineWidth: 3,
        outlineColor: palette().textHalo,
        fontSettings: { sdf: true },
        pickable: false,
        parameters: OVER_EVERYTHING,
      }),
    ];
  }
}
